import React, { useEffect, useState } from "react";
import { Checkbox, Col, Input, Modal, Row, Select, Space, Table, Tooltip, Typography } from "antd";
import { cloneDeep, get, isEmpty, mapKeys, remove, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { langMonth } from "../../../utils/properties";
import { setSection } from "../../../redux/ducks/Section";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CheckOutlined, SyncOutlined, WarningOutlined, StopOutlined } from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import { checkRole, formatNumber, showError, showSuccess } from "../../../utils/utilities";
import "./salApproval.css";
import OrderApproval from "../../../services/api/OrderApproval";
import { setLoading } from "../../../redux/ducks/Loading";
import dayjs from "dayjs";
const { Title } = Typography;

const SalApprovalDetail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const [state, setState] = useState({
    isAdmin: userInfo.roles ? checkRole(userInfo.roles, "SYSTEM_IT") : false,
    // the approver role can be chosen by the system IT or by a user who is both timesheet_approver and pm/super pm
    canChooseApproverRole:
      userInfo.roles &&
      (checkRole(userInfo.roles, "SYSTEM_IT") ||
        (checkRole(userInfo.roles, "TIMESHEET_APPROVER") &&
          (checkRole(userInfo.roles, "PM") || checkRole(userInfo.roles, "SUPER_PM")))),
    salApproverRole: location.state?.approverRole || "TIMESHEET_APPROVER",
    tableContent: [],
    expandedTableContent: [],
    expandedRowKeys: [],
    expandedRow: null,
    syncIsLoading: false,
    submitButtonIsDisabled: true,
    modal: {
      visible: false,
    },
    statusToApprove: null,
    statusAfterApproval: null,
  });

  const columns = [
    {
      title: t("odaAllocation.table.oda"),
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("odaAllocation.table.statusOrder"),
      dataIndex: "statusOrder",
      key: "statusOrder",
    },
    {
      title: t("odaAllocation.table.productCategoryId"),
      dataIndex: "productCategoryId",
      key: "productCategoryId",
    },
    {
      title: t("salApproval.table.costCentreId"),
      dataIndex: "costCentreId",
      key: "costCentreId",
      responsive: ["lg"],
    },
    {
      title: t("odaAllocation.table.hourlyRate"),
      dataIndex: "hourlyRate",
      key: "hourlyRate",
      className: "table-value-right",
      render: (value, row) => {
        if (row.productCategoryType === "HOUR") {
          return formatNumber({
            value,
            lang: currentLanguage,
            suffix: row.productCategoryType,
          });
        }
      },
      responsive: ["lg"],
    },
    {
      title: t("salApproval.table.approvedAmount"),
      dataIndex: "approvedAmount",
      key: "approvedAmount",
      className: "table-value-right",
      render: (value, row) =>
        formatNumber({
          value,
          lang: currentLanguage,
          suffix: row.productCategoryType,
        }),
      responsive: ["md"],
    },
    {
      title: t("salApproval.table.approvedValue"),
      dataIndex: "approvedValue",
      key: "approvedValue",
      className: "table-value-right",
      render: (value) => formatNumber({ value, lang: currentLanguage, suffix: "EUR" }),
    },
    {
      title: "",
      dataIndex: "alert",
      key: "alert",
      render: (value, row) => (
        <Space size="middle">
          {value && <WarningOutlined className="PFred mr-10" />}
          {row.statusOrder === "BLOCKED" && <StopOutlined className="PFred" />}
        </Space>
      ),
    },
  ];

  const isFunctionManager = () => {
    return state.canChooseApproverRole
      ? state.salApproverRole === "TIMESHEET_APPROVER"
      : userInfo.roles && checkRole(userInfo.roles, "TIMESHEET_APPROVER");
  };

  useEffect(() => {
    if (!isEmpty(params.supplierId)) {
      dispatch(
        setSection({
          section: {
            title: `${params.supplierId} - ${location?.state?.name || ""}`,
          },
        })
      );
    } else {
      navigate("/sal-approval");
    }
    getOdaList();
    setApprovalStatuses();
  }, []);

  useEffect(() => {
    getOdaList("syncing");
    setApprovalStatuses();
  }, [state.salApproverRole]);

  const setApprovalStatuses = () => {
    setState((prevState) => ({
      ...prevState,
      statusToApprove: isFunctionManager() ? "WAITING_APPROVAL_1" : "WAITING_APPROVAL_2",
      statusAfterApproval: isFunctionManager() ? "WAITING_APPROVAL_2" : "APPROVED",
    }));
  };

  const expandedRowRender = (record) => {
    const columns = [
      {
        title: t("odaAllocation.table.project"),
        dataIndex: "projectId",
        key: "projectId",
        render: (value, row) => {
          return (
            <div>
              {value} | {row.projectName}
            </div>
          );
        },
        responsive: ["md"],
      },
      {
        title: t("odaAllocation.table.projectTask"),
        dataIndex: "projectTaskId",
        key: "projectTaskId",
        render: (value, row) => {
          return (
            <div>
              {value} | {row.projectTaskName}
            </div>
          );
        },
      },
      {
        title: t("salApproval.table.monthAmount"),
        dataIndex: "amount",
        key: "amount",
        className: "table-value-right",
        render: (value, row) =>
          formatNumber({
            value,
            lang: currentLanguage,
            suffix: state.tableContent[row.fatherKey].productCategoryType,
          }),
        responsive: ["md"],
      },
      {
        title: t("salApproval.table.monthWork"),
        dataIndex: "value",
        key: "value",
        className: "table-value-right",
        render: (value) => formatNumber({ value, lang: currentLanguage, suffix: "EUR" }),
        responsive: ["md"],
      },
      {
        title: t("salApproval.table.supplierNotes"),
        dataIndex: "noteSupplier",
        key: "noteSupplier",
        responsive: ["md"],
        render: (noteSupplier) => <Tooltip title={noteSupplier}>{noteSupplier}</Tooltip>,
      },
      {
        title: t("salApproval.table.functionManagerNotes"),
        dataIndex: "noteTimesheetApprover",
        key: "noteTimesheetApprover",
        render: (noteTimesheetApprover, row) => {
          return (
            <Tooltip title={noteTimesheetApprover}>
              <div>
                <Input
                  disabled={
                    row.oldStatus !== "WAITING_APPROVAL_1" ||
                    (!state.isAdmin && (row.statusProject === "BLOCKED" || row.statusTask === "BLOCKED")) ||
                    !isFunctionManager()
                  }
                  id="noteTimesheetApprover"
                  placeholder={t("salApproval.table.functionManagerNotes")}
                  onChange={(event) => changeApproverNotes(row, event)}
                  value={noteTimesheetApprover}
                />
              </div>
            </Tooltip>
          );
        },
      },
    ];

    if (!isFunctionManager()) {
      columns.push({
        title: t("salApproval.table.pmNotes"),
        dataIndex: "notePm",
        key: "notePm",
        render: (notePm, row) => {
          return (
            <Tooltip title={notePm}>
              <div>
                <Input
                  disabled={
                    row.oldStatus !== "WAITING_APPROVAL_2" ||
                    (!state.isAdmin &&
                      (row.statusProject === "BLOCKED" || row.statusTask === "BLOCKED" || !row.canApprovePm)) || // only the project's PM can approve (if the user is viewing this record only because they are a Super PM, they cannot edit)
                    (state.canChooseApproverRole && state.salApproverRole !== "PM")
                  }
                  id="notePm"
                  placeholder={t("salApproval.table.pmNotes")}
                  onChange={(event) => changeApproverNotes(row, event)}
                  value={notePm}
                />
              </div>
            </Tooltip>
          );
        },
      });
    }
    columns.push({
      title: t("salApproval.table.confirm"),
      dataIndex: "status",
      key: "status",
      width: 50,
      render: (value, record) => {
        return (
          <div className="space-around">
            <Checkbox
              disabled={
                record.oldStatus !== state.statusToApprove ||
                (!state.isAdmin &&
                  (record.statusProject === "BLOCKED" ||
                    record.statusTask === "BLOCKED" ||
                    (state.statusToApprove === "WAITING_APPROVAL_2" && !record.canApprovePm))) // only the project's PM (or the SystemIT) can approve a SAL waiting_approval_2 (if the user is viewing this record only because they are a Super PM, they cannot edit)
              }
              checked={value === state.statusToApprove || value === "REFUTED" ? false : true}
              onChange={(value) => approveSal(record, value)}
            />
          </div>
        );
      },
    });
    return <Table columns={columns} dataSource={state.expandedTableContent[record.key]} pagination={false} />;
  };

  const getOdaList = async (syncing) => {
    if (syncing === "syncing") {
      setState((prevState) => ({
        ...prevState,
        syncIsLoading: true,
      }));
    }
    dispatch(setLoading(true));
    const roleQueryParam = state.canChooseApproverRole && !state.isAdmin ? `&role=${state.salApproverRole}` : "";
    const odaList = await OrderApproval.salApprovalOdaBySupplier(
      `allPages=true&supplierId=${params.supplierId}${roleQueryParam}`
    );
    const allKeys = [];
    let projectTasksList = {};
    if (!odaList.error && !isEmpty(odaList.data)) {
      odaList.data.forEach((data, idx) => {
        data.key = `${idx}`;
        allKeys.push(`${idx}`);
      });
      // eslint-disable-next-line no-undef
      await Promise.all(
        odaList.data.map(async (data) => {
          const returnedValue = await OrderApproval.salApprovalProjectTasksOrder(`orderId=${data.id}${roleQueryParam}`);
          projectTasksList[data.key] = returnedValue.data;
          projectTasksList[data.key].forEach((projectTask, idx) => {
            projectTask.fatherKey = data.key;
            projectTask.key = idx;
            projectTask.oldStatus = projectTask.status;
          });
        })
      );
      setState((prevState) => ({
        ...prevState,
        tableContent: odaList.data,
        expandedRowKeys: allKeys,
        expandedTableContent: projectTasksList,
        submitButtonIsDisabled: true,
      }));
    } else {
      showError(get(odaList, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
    if (syncing === "syncing") {
      setState((prevState) => ({
        ...prevState,
        syncIsLoading: false,
      }));
    }
  };

  const approveSal = (rowData, event) => {
    const selectedProjectTask = cloneDeep(state.expandedTableContent);
    event.target.checked
      ? (selectedProjectTask[rowData.fatherKey][rowData.key].status = state.statusAfterApproval)
      : (selectedProjectTask[rowData.fatherKey][rowData.key].status = "REFUTED");
    setState((prevState) => ({
      ...prevState,
      expandedTableContent: selectedProjectTask,
      submitButtonIsDisabled: checkSubmitButtonIsDisabled(selectedProjectTask),
    }));
  };

  const changeApproverNotes = (rowData, event) => {
    const noteField = isFunctionManager() ? "noteTimesheetApprover" : "notePm";
    const selectedProjectTask = cloneDeep(state.expandedTableContent);
    selectedProjectTask[rowData.fatherKey][rowData.key][noteField] = event.target.value;
    setState((prevState) => ({
      ...prevState,
      expandedTableContent: selectedProjectTask,
      submitButtonIsDisabled: checkSubmitButtonIsDisabled(selectedProjectTask),
    }));
  };

  const checkSubmitButtonIsDisabled = (projectTasks) => {
    let isEnabled = null;
    const noteField = isFunctionManager() ? "noteTimesheetApprover" : "notePm";
    // eslint-disable-next-line no-unused-vars
    Object.entries(projectTasks).forEach(([_, tasks]) => {
      tasks.forEach((task) => {
        if (task.oldStatus === state.statusToApprove) {
          const taskHasNotes = task[noteField] !== null && task[noteField] !== "";
          const taskIsApproved = task.status === state.statusAfterApproval;

          if (taskIsApproved) {
            isEnabled = isEnabled === null ? true : isEnabled;
          } else if (taskHasNotes) {
            isEnabled = isEnabled === null ? true : isEnabled !== false;
          } else {
            isEnabled = false;
          }
        }
      });
    });

    return !isEnabled;
  };

  const openConfirmModal = () => {
    setState((prevState) => ({
      ...prevState,
      modal: { ...prevState, visible: true },
    }));
  };

  const closeConfirmModal = () => {
    setState((prevState) => ({
      ...prevState,
      modal: { ...prevState, visible: false },
    }));
  };

  const submitApproval = async () => {
    setState((prevState) => ({
      ...prevState,
      modal: { ...prevState, visible: false },
    }));
    const request = [];
    mapKeys(state.expandedTableContent, (value) => {
      value.forEach((row) => {
        if (row.oldStatus && row.oldStatus === state.statusToApprove) {
          if (isFunctionManager()) {
            request.push({
              id: row.id,
              noteTimesheetApprover: row.noteTimesheetApprover,
              orderId: row.orderId,
              projectTaskId: row.projectTaskId,
              status: row.status === state.statusToApprove ? "REFUTED" : row.status,
            });
          } else {
            request.push({
              id: row.id,
              notePm: row.notePm,
              orderId: row.orderId,
              projectTaskId: row.projectTaskId,
              status: row.status === state.statusToApprove ? "REFUTED" : row.status,
            });
          }
        }
      });
    });
    dispatch(setLoading(true));
    const submitResponse = await OrderApproval.salApprovalConfirm(request);
    if (!submitResponse.error && submitResponse.data) {
      showSuccess();
      setTimeout(() => getOdaList("syncing"), 1000);
    } else {
      showError(get(submitResponse, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const expandRow = (expanded, record) => {
    const expandedRowKeys = cloneDeep(state.expandedRowKeys);
    if (expanded && expandedRowKeys.indexOf(record.key)) {
      expandedRowKeys.push(record.key);
    } else {
      remove(expandedRowKeys, (key) => key === record.key);
    }
    setState((prevState) => ({ ...prevState, expandedRowKeys }));
  };

  const summaryApprovedBlock = () => {
    let approvedSal = [];
    mapKeys(state.expandedTableContent, (content) => {
      content.map((row) => {
        if (row.status === state.statusAfterApproval && row.oldStatus === state.statusToApprove) {
          approvedSal.push(row);
        }
      });
    });
    if (approvedSal.length > 0) {
      return (
        <>
          <Row>
            <Col span={24}>
              <div className="approved-list-title fw-bold">{t("salApproval.modal.salApproved")}</div>
            </Col>
          </Row>
          <Row>
            <Col span={4}>
              <div className="fw-bold">{t("odaAllocation.table.oda")}</div>
            </Col>
            <Col span={8}>
              <div className="fw-bold">{t("odaAllocation.table.projectTask")}</div>
            </Col>
            <Col span={6}>
              <div className="fw-bold">{t("salApproval.table.monthWork")}</div>
            </Col>
            <Col span={6}>
              <div className="fw-bold">
                {isFunctionManager() ? t("salApproval.table.functionManagerNotes") : t("salApproval.table.pmNotes")}
              </div>
            </Col>
          </Row>
          {approvedSal.map((sal) => {
            return (
              <Row key={sal.key}>
                <Col span={4}>{sal.orderId}</Col>
                <Col span={8}>{sal.projectTaskId + " | " + sal.projectTaskName}</Col>
                <Col span={6}>
                  {formatNumber({
                    value: sal.value,
                    lang: currentLanguage,
                    suffix: "EUR",
                  })}
                </Col>
                <Col span={6}>{isFunctionManager() ? sal.noteTimesheetApprover : sal.notePm}</Col>
              </Row>
            );
          })}
        </>
      );
    }
  };

  const summaryRefusedBlock = () => {
    let refusedSal = [];
    mapKeys(state.expandedTableContent, (content) => {
      content.map((row) => {
        //when status is still the status to approve (WATING_APPROVAL_1 for timesheet approver or WAITING_APPROVAL_2 for PM) it means that the checkbox hasn"t been clicked, so it"s unchecked, so when submitting is REFUTED
        if (
          row.oldStatus === state.statusToApprove &&
          (row.status === state.statusToApprove || row.status === "REFUTED")
        ) {
          refusedSal.push(row);
        }
      });
    });
    if (refusedSal.length > 0) {
      return (
        <>
          <Row>
            <Col span={24}>
              <div className="PFred fw-bold">{t("salApproval.modal.salRefused")}</div>
            </Col>
          </Row>
          <Row>
            <Col span={4}>
              <div className="fw-bold">{t("odaAllocation.table.oda")}</div>
            </Col>
            <Col span={8}>
              <div className="fw-bold">{t("odaAllocation.table.projectTask")}</div>
            </Col>
            <Col span={6}>
              <div className="fw-bold">{t("salApproval.table.monthWork")}</div>
            </Col>
            <Col span={6}>
              <div className="fw-bold">
                {isFunctionManager() ? t("salApproval.table.functionManagerNotes") : t("salApproval.table.pmNotes")}
              </div>
            </Col>
          </Row>
          {refusedSal.map((sal) => {
            return (
              <Row key={sal.key}>
                <Col span={4}>{sal.orderId}</Col>
                <Col span={8}>{sal.projectTaskId + " | " + sal.projectTaskName}</Col>
                <Col span={6}>
                  {formatNumber({
                    value: sal.value,
                    lang: currentLanguage,
                    suffix: "EUR",
                  })}
                </Col>
                <Col span={6}>{isFunctionManager() ? sal.noteTimesheetApprover : sal.notePm}</Col>
              </Row>
            );
          })}
        </>
      );
    }
  };

  return (
    <>
      <Row gutter={[48, 32]} align="middle" justify="space-between">
        {/* Only the System IT can change approver role here (the users that have both roles must choose in the page SalApproval) */}
        {state.isAdmin && (
          <Col lg={7} md={7} xs={24} sm={24}>
            <Row>
              <label className="fw-bold" htmlFor="salApproverRole">
                {t("salApproval.salApproverRole")}
              </label>
            </Row>
            <Row>
              <Select
                id="salApproverRole"
                name="salApproverRole"
                placeholder={t("salApproval.salApproverRole")}
                options={[
                  { value: "TIMESHEET_APPROVER", label: t("salApproval.functionManager") },
                  { value: "PM", label: t("salApproval.pm") },
                ]}
                onChange={(value) => setState((prevState) => ({ ...prevState, salApproverRole: value }))}
                value={state.salApproverRole}
                className="mb-10 mt-10"
                popupMatchSelectWidth={false}
              />
            </Row>
          </Col>
        )}
        <Col span={state.isAdmin ? undefined : 24}>
          <Button
            currentIcon={<SyncOutlined className="icon-large" />}
            clickCallback={() => getOdaList("syncing")}
            isLoading={state.syncIsLoading}
            currentSize="large"
            floatRight={true}
            className="mb-10"
          />
        </Col>
      </Row>
      <Table
        key="salApproval"
        pagination={false}
        columns={columns}
        expandable={{
          expandedRowRender,
          expandedRowKeys: state.expandedRowKeys,
          onExpand: (expanded, record) => expandRow(expanded, record),
        }}
        dataSource={state.tableContent}
        size="small"
        footer={() => (
          <>
            <div className="flex-center">
              <div>{t("salApproval.table.date")}:</div>
              <div className="current-month-container">
                {!isEmpty(state.tableContent)
                  ? dayjs(state.tableContent[0].currentMonth.toString(), "YYYYMM").format(dateFormat)
                  : ""}
              </div>
            </div>
            <Button
              currentIcon={<CheckOutlined className="icon-large" />}
              currentSize="large"
              clickCallback={() => openConfirmModal()}
              className="right"
              isDisabled={state.submitButtonIsDisabled}
            />
          </>
        )}
      />
      <Modal
        open={state.modal.visible}
        width={700}
        title={t("salApproval.modal.title")}
        footer={[
          <Button
            key="sal-approve"
            currentIcon={<CheckOutlined className="icon-medium" />}
            clickCallback={() => submitApproval()}
          />,
        ]}
        onCancel={closeConfirmModal}
      >
        <Space direction="vertical">
          <Title level={5}>{t("salApproval.modal.subtitle")}</Title>
        </Space>
        <>
          {summaryApprovedBlock()}
          {summaryRefusedBlock()}
        </>
      </Modal>
    </>
  );
};

export default SalApprovalDetail;
